import { useEffect, useState } from 'react';
import { FileText, Hash, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from 'components/ui/command';
import { Dialog, DialogContent, DialogTitle } from 'components/ui/dialog';
import { docsNavSections } from '../docs-nav-data';
import { getAllDocs } from '../utils/load-docs';
import { searchDocs } from '../utils/search-docs';

export function DocsSearch() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const results = searchDocs(getAllDocs(), query);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setIsOpen((open) => !open);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) setQuery('');
  };

  const handleSelect = (slug: string, headingId?: string) => {
    handleOpenChange(false);
    navigate(headingId ? `/docs/${slug}#${headingId}` : `/docs/${slug}`);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="flex w-full min-w-0 items-center gap-2 rounded-md border border-input bg-muted/40 px-3 py-2 text-sm text-muted-foreground hover:bg-muted"
      >
        <Search className="size-4 shrink-0" />
        <span className="flex-1 truncate text-left">Search...</span>
        <kbd className="hidden rounded border border-border bg-background px-1.5 py-0.5 text-xs sm:inline">
          ⌘K
        </kbd>
      </button>

      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent className="overflow-hidden p-0 shadow-lg" aria-describedby={undefined}>
          <DialogTitle className="sr-only">Search the docs</DialogTitle>
          {/* Results are matched by searchDocs, so cmdk's own fuzzy filter is turned off. */}
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search the docs..."
              value={query}
              onValueChange={setQuery}
              className="pr-8"
            />
            <CommandList className="max-h-[60vh] p-1">
              {query.trim() && (
                <CommandEmpty>No results for &quot;{query.trim()}&quot;.</CommandEmpty>
              )}
              {/* Before anything is typed, list every page so the dialog doubles as a quick jump menu. */}
              {!query.trim() &&
                docsNavSections.map((section) => (
                  <CommandGroup key={section.title} heading={section.title}>
                    {section.links.map((link) => (
                      <CommandItem
                        key={link.slug}
                        value={link.slug}
                        onSelect={() => handleSelect(link.slug)}
                        className="justify-start gap-3"
                      >
                        <FileText />
                        <span className="truncate">{link.label}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                ))}
              {results.length > 0 && (
                <CommandGroup heading="Results">
                  {results.map((result) => (
                    <CommandItem
                      key={`${result.slug}#${result.headingId ?? ''}`}
                      value={`${result.slug}#${result.headingId ?? ''}`}
                      onSelect={() => handleSelect(result.slug, result.headingId)}
                      className="h-auto items-start justify-start gap-3 py-2"
                    >
                      {result.headingId ? (
                        <Hash className="mt-0.5 shrink-0" />
                      ) : (
                        <FileText className="mt-0.5 shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-foreground">
                          {result.pageTitle}
                          {result.headingText && (
                            <span className="text-muted-foreground"> › {result.headingText}</span>
                          )}
                        </p>
                        <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                          {result.snippet}
                        </p>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </DialogContent>
      </Dialog>
    </>
  );
}
